import {
  FacilityOrder,
  FacilityOrderBag,
} from '@/providers/laundryheap/interfaces/facility-orders';
import {
  addAssociatedTag,
  deleteAssociatedTag,
  deleteAssociatedTags,
  getFacilityOrder,
  getFacilityOrderBag,
  getFacilityProblemsList,
  getFacilityServices,
  getSupportCenterOrderList,
  signInFacilityWorker,
  watchFacilityOrderBag,
  watchReceivedSupportCenterOrderList,
} from '@/providers/laundryheap/api';
import { FacilityService } from '@/providers/laundryheap/interfaces/facility-services';
import {
  AddAssociatedTagData,
  DeleteAssociatedTagData,
  DeleteAssociatedTagsData,
} from '@/providers/laundryheap/types';
import { FacilityProblem } from '@/providers/laundryheap/interfaces/problems';
import { FacilityWorkerToken } from '@/providers/laundryheap/interfaces/facility-worker';
import { ApiAlreadyNotified } from '@/errors/api-already-notified';
import { OrdersWithProblem } from '@/providers/laundryheap/interfaces/support';
import client from '@/providers/laundryheap/client';

/**
 * Login facility worker with the token
 * @param token
 */
export const loginFacilityWorker = async (
  token: string
): Promise<FacilityWorkerToken> => {
  const apiData = { token: token };
  const facilityWorker = await signInFacilityWorker(apiData);
  if (!facilityWorker.status || !facilityWorker.data) {
    throw new ApiAlreadyNotified();
  }

  return facilityWorker.data;
};

/**
 * Fetch facility order data from api or throw an error
 * @param facilityOrderId
 */
export const fetchFacilityOrder = async (
  facilityOrderId: string
): Promise<FacilityOrder> => {
  const apiData = { id: facilityOrderId };
  const facilityOrder = await getFacilityOrder(apiData);
  if (!facilityOrder.status || !facilityOrder.data) {
    throw new ApiAlreadyNotified();
  }

  return facilityOrder.data;
};

/**
 * Fetch facility bag data from api or throw an error
 * @param bagId
 * @param forceReFetch - If forcing fetch without cache
 */
export const fetchFacilityOrderBag = async (
  bagId: string,
  forceReFetch = false
): Promise<FacilityOrderBag> => {
  const apiData = { id: bagId };
  const facilityOrderBag = forceReFetch
    ? await getFacilityOrderBag(apiData)
    : await watchFacilityOrderBag(apiData);
  if (!facilityOrderBag.status || !facilityOrderBag.data) {
    throw new ApiAlreadyNotified();
  }

  return facilityOrderBag.data;
};

/**
 * Return fetched services for the bag or throw an error
 * @param bagId
 */
export const fetchFacilityServices = async (
  bagId: string
): Promise<FacilityService[]> => {
  const apiData = { bagId: bagId };
  const facilityServices = await getFacilityServices(apiData);
  if (!facilityServices.status || !facilityServices.data) {
    throw new ApiAlreadyNotified();
  }

  return facilityServices.data;
};

/*****************************************
 * PROBLEM REPORTING
 *****************************************/

/**
 * Problem reporting - Fetch list for reporting a problems
 */
export const fetchFacilityProblemsList = async (): Promise<
  FacilityProblem[]
> => {
  const FacilityProblems = await getFacilityProblemsList();
  if (!FacilityProblems.status || !FacilityProblems.data) {
    throw new ApiAlreadyNotified();
  }

  return FacilityProblems.data;
};

/**
 * Itemisation - Add/connect new qr tag to the order
 */
export const connectFacilityOrderBagTag = async (
  bagId: string,
  qrCode: string,
  serviceShortCode: string
): Promise<FacilityOrderBag> => {
  const data: AddAssociatedTagData = {
    bagId,
    qrCode,
    serviceShortCode,
  };
  const associatedTag = await addAssociatedTag(data);
  if (!associatedTag.status || !associatedTag.data) {
    throw new ApiAlreadyNotified();
  }

  return associatedTag.data;
};

/**
 * Itemisation - Remove connected qr tag from the order
 */
export const removeConnectedFacilityOrderBagTag = async (
  tagId: string
): Promise<FacilityOrderBag> => {
  const data: DeleteAssociatedTagData = { tagId };
  const removeTag = await deleteAssociatedTag(data);

  if (!removeTag.status || !removeTag.data) {
    throw new ApiAlreadyNotified();
  }

  return removeTag.data;
};

/**
 * Itemisation - Remove multiple connected qr tags from the order
 */
export const removeConnectedFacilityOrderBagTags = async (
  tagIds: string[]
): Promise<FacilityOrderBag> => {
  const data: DeleteAssociatedTagsData = { tagIds };
  const removeTag = await deleteAssociatedTags(data);

  if (!removeTag.status || !removeTag.data) {
    throw new ApiAlreadyNotified();
  }

  return removeTag.data;
};

/***********************
 * Support
 **********************/
export const fetchSupportCenterList = async (): Promise<OrdersWithProblem> => {
  const support = await getSupportCenterOrderList();
  if (!support.status || !support.data) {
    throw new ApiAlreadyNotified();
  }

  return support.data;
};

export const watchCountReceivedSupportCenter = async (): Promise<number> => {
  const support = await watchReceivedSupportCenterOrderList();
  if (!support.status || !support.data) {
    throw new ApiAlreadyNotified();
  }

  return support.data.received.length;
};

// As previous method starts polling, we need to stop it on unmount
export const stopWatchCountReceivedSupportCenter = (): void => {
  client.getObservableQueries().forEach((q) => {
    if (q.queryName !== 'ordersWithProblems') {
      return;
    }

    q.stopPolling();
  });
};
